import { RootState } from '../../../types/rootState'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectHasHomebotNetworkFeatureFlag, selectNativeAccessFeature } from '../../../store/selectors/customerProfile'
import { selectScheduleTourShowModal } from '../../../store/slices/scheduleTour'
import ScheduleTourWizard from './ScheduleTour/ScheduleTourWizard'
import { RouteComponentProps, useLocation } from 'react-router'
import { setSelectedListingId } from '../../../actions/listings'
import { useTrackingContext } from '../../../providers/tracking'
import { setActiveNavigationTab } from '../../../store/slices/navigation'
import { NAVIGATION_TAB_NAMES } from '../../../types/navigation'
import { SimilarHomes } from './SimilarHomes/SimilarHomes'
import { DownloadMobileAppPromptCard } from './DownloadMobileAppPromptCard/Desktop/DownloadMobileAppPromptCard'
import { HomeSearchFooter } from '../HomeSearchFooter/HomeSearchFooter'
import { Box, Flex, LoadingSpinner } from '@homebotapp/hb-react-component-catalog'
import { MobileDownloadMobileAppPromptCard } from './DownloadMobileAppPromptCard/Mobile/MobileDownloadMobileAppPromptCard'
import useViewport from '../../../hooks/useViewport'
import { Description } from './Description/Description'
import { HomeDetails } from './HomeDetails/HomeDetails'
import { LocationMap } from './LocationMap/LocationMap'
import { StatusHeader } from './StatusHeader/StatusHeader'
import { PropertyStats } from './PropertyStats/PropertyStats'
import { RealEstateAgentCard } from './RealEstateAgentCard/RealEstateAgentCard'
import { useGetListing } from '../../../hooks/listings/useGetListing'
import { useCreateClientEvent } from '../../../hooks/useCreateClientEvent'
import { ClientEventName, ClientEventSource } from '../../../api/gqlaxy/gql/generated/graphql'
import { EstimatedMonthlyPayment } from './EstimatedMonthlyPayment/EstimatedMonthlyPayment'
import { fetchLatestMortgageRates } from '../../../actions/latestMortgageRates'
import { formatStreetAddress } from '../../../helpers/formatListingAddress'
import { ListingDetailsPhotos } from './PhotoPanel/ListingDetailsPhotos/ListingDetailsPhotos'
import { ListingActionBar } from './ListingActionBar/ListingActionBar'
import { defineMessages, useIntl } from 'react-intl'
import { DownForMaintenance } from '../../shared/DownForMaintenance/DownForMaintenance'
import { HomeHighlights } from './HomeHighlights/HomeHighlights'
import { useEffectOncePerSessionConditionally } from '../../../hooks'
import { OpenHouseOrScheduleTour } from './OpenHouseOrScheduleTour/OpenHouseOrScheduleTour'
import { ListingMessageCardContainer } from './ListingMessageCard/ListingMessageCardContainer'

export interface ListingDetailsMatchParams {
  listingId: string
}

const MSG = defineMessages({
  notFoundHead: {
    id: 'ListingDetails.notFoundHead',
    defaultMessage: 'This listing could not be found'
  },
  notFoundBody: {
    id: 'ListingDetails.notFoundBody',
    defaultMessage: 'It has most likely been removed from the MLS.'
  }
})

export const ListingDetails = ({ match }: RouteComponentProps<ListingDetailsMatchParams>) => {
  const { listingId } = match.params
  const { isMediumOrAbove } = useViewport()
  const { mutateAsync: createClientEvent } = useCreateClientEvent({ disableErrorPrompts: true })
  const dispatch = useDispatch()
  const [index, setIndex] = useState(1)
  const [downPaymentAmountDollars, setDownPaymentAmountDollars] = useState<number | undefined>(
    parseInt(localStorage.getItem('downPaymentCash') || '') || undefined
  )
  const { isLoading, data, refetch, isRefetching } = useGetListing(
    {
      listingId: listingId,
      options: {
        downpaymentAmountDollars: downPaymentAmountDollars ? Math.round(downPaymentAmountDollars) : undefined
      }
    },
    {
      enabled: !!listingId
    }
  )

  const listing = data?.listingEnriched?.listing
  const address = listing?.address && formatStreetAddress(listing.address)
  const loanInfo = data?.listingEnriched?.loanInfo
  const bucketedMonthlyHomeExpenses = data?.listingEnriched?.bucketedMonthlyHomeExpenses
  const assumableMortgageMonthlyHomeExpenses = data?.listingEnriched?.assumableMortgageMonthlyHomeExpenses
  const assumableMortgageLoanInfo = data?.listingEnriched?.assumableMortgageLoanInfo
  const assumableMortgageSecondLoanInfo = data?.listingEnriched?.assumableMortgageSecondLoanInfo
  const showModal = useSelector(selectScheduleTourShowModal)
  const hasNativeAccess = useSelector<RootState, boolean>(selectNativeAccessFeature)
  const { trackingClient } = useTrackingContext()
  const ampliEventRef = useRef(false)
  const propertyDescriptionRef = useRef<HTMLDivElement>(null)
  const intl = useIntl()
  const isCustomerOnHomebotNetwork = useSelector(selectHasHomebotNetworkFeatureFlag)

  const [assumableToggle, setAssumableToggle] = useState(false)

  const { pathname } = useLocation()

  useEffect(() => {
    if (listingId) {
      refetch()
      window.scrollTo(0, 0)
    }
  }, [listingId, refetch])

  useEffect(() => {
    if (localStorage.getItem('downPaymentCash')) {
      setDownPaymentAmountDollars(parseInt(localStorage.getItem('downPaymentCash') || ''))
    } else if (localStorage.getItem('downPaymentPercentage')) {
      const percent = parseFloat(localStorage.getItem('downPaymentPercentage') || '0') / 100
      const listPrice = listing?.priceCents ? listing?.priceCents / 100 : 0

      setDownPaymentAmountDollars(listPrice * percent)
    }
  }, [listing?.priceCents])

  useEffect(() => {
    dispatch(fetchLatestMortgageRates())
  }, [dispatch])

  useEffect(() => {
    // Set the active tab to home search when we are on the
    // listing details page.
    dispatch(setActiveNavigationTab(NAVIGATION_TAB_NAMES.SEARCH))
    dispatch(setSelectedListingId(listing?.id))
  }, [dispatch, listing?.id])

  // --------- Create listing viewed event START

  const createListingViewEvent = useCallback(() => {
    if (!listing?.id) {
      return
    }

    createClientEvent({
      clientEventInput: {
        eventName: ClientEventName.View,
        eventSource: ClientEventSource.ListingDetailed,
        listingId: listing?.id
      }
    })
  }, [listing, createClientEvent])

  useEffectOncePerSessionConditionally(createListingViewEvent, 'View_ListingsDetails', Boolean(listing?.id))

  // deprecated, moving to a single "viewed" client event
  useEffect(() => {
    if (listing?.id) {
      createClientEvent({
        clientEventInput: {
          eventName: ClientEventName.DetailsPageViewedEvent,
          listingId: listing?.id
        }
      })
    }
  }, [listing?.id])

  // --------- Create listing viewed event END

  useEffect(() => {
    const canControlScrollRestoration = 'scrollRestoration' in window.history
    if (canControlScrollRestoration) {
      window.history.scrollRestoration = 'manual'
    }

    window.scrollTo(0, 0)
  }, [pathname])

  useEffect(() => {
    if (isLoading === false && ampliEventRef.current === false) {
      trackingClient?.listingViewed({
        building_size: listing?.sqftTotal,
        city: listing?.address?.city || undefined,
        mls_number: listing?.mlsNumber,
        num_baths: listing?.bathroomsCount || undefined,
        num_beds: listing?.bedroomsCount || undefined,
        price_per_sqft: listing?.priceCents
          ? Math.ceil(listing?.priceCents / 100 / (listing?.sqftTotal || listing?.sqftLot || 1))
          : undefined,
        price: listing?.priceCents ? listing?.priceCents / 100 : undefined,
        property_type: listing?.propertyType,
        source: listing?.originatingSystemName,
        state_or_province: listing?.address?.stateOrProvince,
        status: listing?.status,
        year_built: listing?.yearBuilt || undefined,
        zip_code: listing?.address?.postalCode
      })

      ampliEventRef.current = true
    }
  }, [listing, trackingClient])

  if (!listing?.id && !isLoading) {
    return (
      <DownForMaintenance head={intl.formatMessage(MSG.notFoundHead)} body={intl.formatMessage(MSG.notFoundBody)} />
    )
  }

  if (isLoading) {
    return (
      <Flex
        align='center'
        justify='center'
        h='calc(100vh - (var(--banner-height, 0px) + var(--top-nav-bar-height, 0px) + 48px))'
      >
        <LoadingSpinner variant='bot-head' size='lg' />
      </Flex>
    )
  }

  return (
    <>
      <Box bg='black'>
        {listing && <ListingActionBar listing={listing} />}
        {listing && <ListingDetailsPhotos imageChange={current => setIndex(current + 1)} listing={listing} />}
        {/* @ts-ignore */}
        <Box padding={[2, 4]} background='var(--hb-colors-chakra-body-bg)'>
          <Box maxW='67rem' margin='auto' pl='env(safe-area-inset-left, 0)'>
            <Flex direction={['column', null, null, 'row']} justify='space-between' gap={4}>
              <Box flexBasis='66.66%'>
                <StatusHeader listing={listing} />
                <PropertyStats listing={listing} />
                <HomeHighlights
                  listing={listing}
                  assumableMortgageLoanInfo={assumableMortgageLoanInfo}
                  propertyDescriptionRef={propertyDescriptionRef}
                />
                <Description listing={listing} propertyDescriptionRef={propertyDescriptionRef} />
                <Box mt={8} display={['block', null, null, 'none']}>
                  <OpenHouseOrScheduleTour listing={listing} />
                </Box>
                <EstimatedMonthlyPayment
                  address={address}
                  loanInfo={loanInfo}
                  listingId={listingId}
                  homePrice={listing?.priceCents ? listing?.priceCents / 100 : 0}
                  setDownPaymentAmountDollars={setDownPaymentAmountDollars}
                  bucketedMonthlyHomeExpenses={bucketedMonthlyHomeExpenses} // change based on assumable
                  isAssumable={listing?.isAssumable}
                  assumableToggle={assumableToggle}
                  setAssumableToggle={setAssumableToggle}
                  assumableMortgageMonthlyHomeExpenses={assumableMortgageMonthlyHomeExpenses}
                  assumableMortgageLoanInfo={assumableMortgageLoanInfo}
                  assumableMortgageSecondLoanInfo={assumableMortgageSecondLoanInfo}
                />

                {listing?.address?.latitude &&
                  listing?.address?.longitude &&
                  listing.internetAddressDisplayYn !== false &&
                  listing.showMapLink !== false && <LocationMap listing={listing} showHeading />}
                <HomeDetails listing={listing} />
                {/* !isPublic && <LoanOfficerCard listing={listing} /> */}
              </Box>
              <Box
                left={2}
                right={2}
                zIndex={2}
                flexBasis='33.33%'
                position={['fixed', null, null, 'relative']}
                bottom={['calc(4rem + env(safe-area-inset-bottom))', null, null, 0]}
              >
                <Box display={['none', null, null, 'block']}>
                  <OpenHouseOrScheduleTour listing={listing} />
                </Box>
                {isCustomerOnHomebotNetwork ? (
                  <ListingMessageCardContainer listing={listing} />
                ) : (
                  <RealEstateAgentCard listing={listing} />
                )}
              </Box>
            </Flex>
            {data?.listingEnriched?.similarListings && (
              <SimilarHomes listingId={listingId} similarListings={data.listingEnriched.similarListings} />
            )}
            {hasNativeAccess && (
              <Box mt={8}>
                {isMediumOrAbove ? <DownloadMobileAppPromptCard /> : <MobileDownloadMobileAppPromptCard />}
              </Box>
            )}
            <HomeSearchFooter listing={listing} />
          </Box>
        </Box>
      </Box>
      {showModal && listing && <ScheduleTourWizard listing={listing} />}
    </>
  )
}
